import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { FormGroup, Validators, AbstractControl } from "@angular/forms";
import { TitleCasePipe } from "@angular/common";

import { Observable, from, of, Subscription } from "rxjs";
import { switchMap, map, catchError, finalize, tap } from "rxjs/operators";
import { ApplicationState, ApplicationSteps } from "../../../../models/applications.models";
import { GISPointTypes } from "../../../../../common/models/well.model";
import { UpdatedGISValue } from "../../../../../models/request.result";
import { GisQueryService } from "../../../../../gis/services/gis-query.service";
import { GisTokenService } from "../../../../../gis/services/gis-token.service";
import { LoaderService } from "../../../../../common/services/loader.service";
import { DynamicDialogService } from "../../../../../core/services/dynamic-dialog.service";
import { ApplicationSectionBaseComponent } from "../../../application-section-base/application-section-base.component";
import { AuthService } from "../../../../../services/auth.service";
import { FormationModel } from "../../../../../form-controls/components/new-casing-plan/models/casing-plan.model";



@Component({
    selector: 'app-well-information',
    templateUrl: './well-information.component.html',
    styleUrls: ['./well-information.component.scss', '../../../../../core/components/collapsible/collapsible.component.scss', '../../../application-section-base/application-section-base.component.scss'],
    providers: [GisQueryService, GisTokenService],
    standalone: false
})
export class WellInformationComponent extends ApplicationSectionBaseComponent implements OnInit, OnDestroy {

    readonly step: ApplicationSteps = ApplicationSteps.WellInformation;

    @Input() agencies: any[];

    get disableValidateButton(): boolean {
        return this.step != this.activeStep || this.reviewStates.includes(this.applicationState) || (this.applicationState == ApplicationState.Corrections && this.form.get('surfaceLatitude').pristine && this.form.get('surfaceLongitude').pristine);
    }

    @Input() formations: FormationModel[];;

    private _orientation: string;

    get orientation(): string {
        return this._orientation;
    }

    @Input() set orientation(value: string) {
        this._orientation = value;
    }

    pointTypes = GISPointTypes;

    subscription: Subscription = new Subscription();

    @Output() urbanizationChanged: EventEmitter<string> = new EventEmitter<string>();

    constructor(private queryService: GisQueryService, private casePipe: TitleCasePipe, private _loader: LoaderService, private dialogService: DynamicDialogService, _auth: AuthService) { super(_auth); }

    ngOnInit() {
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }



    queryCounty(pointType: GISPointTypes): Observable<boolean> {
        let latLong: number[];
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(this.form.get('surfaceLatitude').value), Number(this.form.get('surfaceLongitude').value)]
                break;
            case GISPointTypes.Target:
                latLong = [Number(this.form.get('targetLatitude').value), Number(this.form.get('targetLongitude').value)]
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Counties", latLong);
        return from(query).pipe(
            map((res: any) => {
                let verified: boolean = false;
                if (res != null && res.features != null && res.features.length > 0) {
                    let resolvedCountyName: string = res.features[0].attributes["COUNTY"] || "";
                    let resolvedCountyNumber: number = parseInt(res.features[0].attributes["FIPS"], 10);
                    this.form.get('countyName').setValue(resolvedCountyName);
                    this.form.get('countyNumber').setValue(resolvedCountyNumber);
                    verified = true;
                }
                return verified;
            })
        );
    }

    querySectionLotTract(pointType: GISPointTypes): Observable<UpdatedGISValue[]> {
        let latLong: number[];
        let originalSection: any = "";
        let originalLot: any = "";
        let originalTract: any = "";
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(this.form.get('surfaceLatitude').value), Number(this.form.get('surfaceLongitude').value)];
                break;
            case GISPointTypes.Target:
                latLong = [Number(this.form.get('targetLatitude').value), Number(this.form.get('targetLongitude').value)];
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "land_subdivisions_old", latLong);
        return from(query).pipe(
            map((result: any) => {
                let updates: UpdatedGISValue[] = [];
                let sectionChange: UpdatedGISValue = new UpdatedGISValue("Section", originalSection, "");
                let lotChange: UpdatedGISValue = new UpdatedGISValue("Lot", originalLot, "");
                let tractChange: UpdatedGISValue = new UpdatedGISValue("Tract", originalTract, "");
                if (result != null && result.features != null && result.features.length > 0) {
                    const data = result.features[0].attributes;
                    let sectionComponent: AbstractControl = null;
                    let lotComponent: AbstractControl = null;
                    let tractComponent: AbstractControl = null;
                    let townshipNumberComponent: AbstractControl = null;
                    switch (pointType) {
                        case GISPointTypes.WellHead:
                            sectionComponent = this.form.get('section');
                            lotComponent = this.form.get('lot');
                            tractComponent = this.form.get('tract');
                            townshipNumberComponent = this.form.get("civilTownshipNumber");
                            break;

                        case GISPointTypes.Target:
                            sectionComponent = this.form.get('targetSection');
                            lotComponent = this.form.get('targetLot');
                            tractComponent = this.form.get('targetTract');
                            break;
                    }
                    if (data["SECTION_"] != null) {
                        let section = data["SECTION_"].toString().trim().length > 0 ? data["SECTION_"] : "";
                        sectionChange.correctedValue = section;
                        sectionComponent.setValue(section);
                    }
                    if (data["LOT"] != null) {
                        let lot = data["LOT"].toString().trim().length > 0 ? data["LOT"] : "";
                        lotChange.correctedValue = lot;
                        lotComponent.setValue(lot);
                    }
                    if (data["TRACT"] != null) {
                        let tract = data["TRACT"].toString().trim().length > 0 ? data["TRACT"] : "";
                        tractComponent.setValue(tract);
                    }
                    if (data["CVLTWP_NO"] != null) {
                        let townshipNumber = data["CVLTWP_NO"].toString().trim().length !== 0 ? data["CVLTWP_NO"] : "";
                        townshipNumberComponent.setValue(townshipNumber);
                    }
                }
                updates.push(sectionChange);
                updates.push(lotChange);
                updates.push(tractChange);
                return updates;
            }
            )
        );
    }

    queryTownship(pointType: GISPointTypes): Observable<UpdatedGISValue> {
        let latLong: number[];
        let originalValue: any = "";
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(this.form.get('surfaceLatitude').value), Number(this.form.get('surfaceLongitude').value)];
                break;
            case GISPointTypes.Target:
                latLong = [Number(this.form.get('targetLatitude').value), Number(this.form.get('targetLongitude').value)];
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Townships", latLong);
        return from(query).pipe(
            map((res: any) => {
                let result: UpdatedGISValue = new UpdatedGISValue("Civil Township", originalValue, "");
                if (res != null && res.features != null && res.features.length > 0) {
                    let component: AbstractControl = null;
                    switch (pointType) {
                        case GISPointTypes.WellHead:
                            component = this.form.get('civilTownship');
                            break;
                    }
                    let resolvedTownshipName: string = res.features[0].attributes["TOWNSHIP"];
                    if (resolvedTownshipName.toLocaleUpperCase() === resolvedTownshipName) {
                        resolvedTownshipName = this.casePipe.transform(resolvedTownshipName);
                    }
                    result.correctedValue = resolvedTownshipName;
                    component.setValue(resolvedTownshipName);
                }
                return result;
            })
        )
    }

    queryUrban(pointType: GISPointTypes): Observable<string> {
        let latLong: number[];
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(this.form.get('surfaceLatitude').value), Number(this.form.get('surfaceLongitude').value)]
                break;
            case GISPointTypes.Target:
                latLong = [Number(this.form.get('targetLatitude').value), Number(this.form.get('targetLongitude').value)]
                break;
        }
        let query = this.queryService.getPopulationByCoordinate(latLong[1], latLong[0]);
        return from(query).pipe(
            map((res: any) => {
                let urban: string = "NONURB";
                if (res != null && res["Population"] !== "ERROR") {
                    if (res["ZoneType"] == "VILLAGE" || res["ZoneType"] == "CITY") urban = "URBMCP";
                    else if (res["ZoneType"] == "TOWNSHIP" && res["Population"] != "NONURB") {
                        const population = Number(res["Population"]);
                        if (population >= 5000 && population < 10000) urban = "URB5000";
                        else if (population >= 10000 && population < 15000) urban = "URB10000";
                        else if (population > 15000) urban = "URBMCP";
                    }
                }
                return urban;
            })
        );
    }

    surfaceRightsChanged(value: boolean | string): void {
        if (value == true || value == "true") {
            this.form.get("agency").enable();
            this.form.get("agency").setValidators([Validators.required]);
        }
        else {
            this.form.get("agency").disable();
            this.form.get("agency").setValue([]);
            this.form.get("agency").clearValidators();
        }
        this.form.get("agency").updateValueAndValidity();
    }

    toggleOpen(): void {
        if (this.step == this.activeStep || !this.isOpen) super.toggleOpen();
        this.openChanged.emit(this.step);
    }

    validateSurface(pointType: GISPointTypes): void {
        let query: Observable<any> = this.queryCounty(pointType).pipe(
            switchMap((res: boolean) => {
                if (res === true) {
                    return this.queryTownship(pointType);
                } else {
                    return of(null);
                }
            }),
            switchMap((value: UpdatedGISValue) => {
                if (value != null) {
                    return this.querySectionLotTract(pointType).pipe(
                        map((updates: UpdatedGISValue[]) => {
                            updates.push(value);
                            return updates;
                        })
                    )
                } else {
                    return of(null);
                }
            }),
            switchMap(() => {
                return this.queryUrban(pointType).pipe(
                    tap((urban: string) => {                      
                        this.urbanizationChanged.emit(urban);
                    })
                )
            })
        );
        this._loader.toggleLoader(true);
        let query$ = query.pipe(
            catchError((err) => {
                console.error(err);
                this.dialogService.showGISError();
                return of(null);
            }),
            finalize(() => { this._loader.toggleLoader(false); })
        ).subscribe(
            () => {
                this.form.get("surfaceGISValidationRun").setValue(true);
                this.form.updateValueAndValidity();
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(query$);
    }
}
